import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import RouterLink from 'react-router/lib/Link';
import oneLine from 'common-tags/lib/oneLine';

import connectUI from '../../basics/ui/UIConnector';
import suitcss from '../../../helpers/suitcss';
import { toFullCurrency } from '../../../helpers/money';
import { getPaymentFeeDuration } from '../../../helpers/entity';
import { shape as tariffShape, CATEGORY_SINGLE_PRODUCT } from '../../../propTypes/tariff';
import Headline from '../../basics/text/TextHeadline';
import Copy from '../../basics/text/TextCopy';
import Link from '../../basics/text/TextLink';
import Unit from '../../basics/text/TextUnit';
import SvgLoader from '../../basics/media/MediaSvgLoader';
import Callout from '../../../containers/callout/Callout';
import FootnoteReference from '../../../containers/footnote/FootnoteReference';
import { getAriaLabelFromTemplate } from '../../../helpers/tariff';

class TariffItemPanel extends PureComponent {

  renderPaymentFee({ utilities }) {
    const { inverted, tariff, ui } = this.props;
    return (
      <div className={suitcss({ descendantName: 'paymentFee', utilities }, this)} >
        <Unit
          postfix={[
            getPaymentFeeDuration(tariff, ui, true),
            <FootnoteReference id={tariff.eid} />,
          ]}
          price={tariff.paymentFee}
          size="s"
          inverted={inverted}
        />
      </div>
    );
  }

  renderSinglePaymentFee({ utilities }) {
    const { ui, tariff, inverted } = this.props;
    return (
      <div className={suitcss({ descendantName: 'singlePaymentFee', utilities }, this)} >
        <Copy
          inverted={inverted}
          embedded
          raw
        >
          {`${ui.guiFeeSingular} ${toFullCurrency(tariff.singlePaymentFee)}`}
        </Copy>
      </div>
    );
  }

  renderHeader() {
    const { linkPrimary, moduleId } = this.props;
    const {
      category,
      type,
      icon,
      name,
      singlePaymentFee,
    } = this.props.tariff;
    const isSingleProduct = category === CATEGORY_SINGLE_PRODUCT;
    const typeCopy = !name ? type : oneLine`
        ${type.replace('Internet-', 'Internet-</br>')}
        <span class="u-lHidden u-weightBold u-colorPrimary">${name}</span>
    `;
    const equalizerGroup = `${moduleId}-tariffItemPanelHeader`;

    return (
      <RouterLink
        className={suitcss({
          descendantName: 'header',
        }, this)}
        data-eq-group-l={equalizerGroup}
        data-eq-group-m={equalizerGroup}
        to={linkPrimary.to}
      >
        <div className={suitcss({ descendantName: 'title' }, this)} >
          <div className={suitcss({ descendantName: 'type' }, this)} >
              <Headline
                size="m"
                utilities={['colorDefault']}
                embedded
                raw
              >
                {typeCopy}
              </Headline>
          </div>
          {(icon || name) && (
            <div className={suitcss({ descendantName: 'name', utilities: ['smHidden'] }, this)} >
              {(icon) && (<SvgLoader path={icon} />)}
              {!icon && (
                <Headline
                  size="xxl"
                  utilities={['weightBold', 'colorPrimary']}
                  embedded
                  raw
                >
                  {name}
                </Headline>
              )}
            </div>
          )}
        </div>
        {this.renderPaymentFee({ utilities: ['lHidden'] })}
        {isSingleProduct && singlePaymentFee &&
          this.renderSinglePaymentFee({ utilities: ['lHidden'] })
        }
      </RouterLink>
    );
  }

  renderContent() {
    const {
      inverted,
      productInformationSheetLink,
      ui,
      tariff,
    } = this.props;

    const {
      headline,
      subline,
      copy,
    } = this.props.tariff;

    const pibArialLabel = getAriaLabelFromTemplate(
      ui.guiInfotypeForTariff,
      productInformationSheetLink.title,
      tariff,
    );

    return (
      <div className={suitcss({ descendantName: 'content' }, this)} >
        <div className={suitcss({ descendantName: 'headline' }, this)} >
          <Headline
            raw
            size="m"
            utilities={['lowercase']}
            inverted={inverted}
            embedded
          >
            {headline}
          </Headline>
        </div>
        {subline && (
          <div className={suitcss({ descendantName: 'subline' }, this)} >
            <Headline
              size="s"
              inverted={inverted}
              lowercase
              embedded
              raw
              element="div"
            >
              {subline}
            </Headline>
          </div>
        )}
        <div className={suitcss({ descendantName: 'copy' }, this)} >
          <Copy
            inverted={inverted}
            embedded
            raw
          >
            {copy}
          </Copy>
        </div>
        <Link
          className={suitcss({ descendantName: 'productInformation' }, this)}
          href={productInformationSheetLink.file.url}
          withoutArrow
          element="a"
          target={'_blank'}
          ariaLabel={pibArialLabel}
        >
          {productInformationSheetLink.copy}
        </Link>
      </div>
    );
  }

  renderSubContent() {
    const { linkPrimary, tariff } = this.props;
    const { category, singlePaymentFee } = tariff;
    const isSingleProduct = category === CATEGORY_SINGLE_PRODUCT;

    return (
      <div className={suitcss({ descendantName: 'subcontent' }, this)} >
        {this.renderPaymentFee({ utilities: ['smHidden'] })}
        {isSingleProduct && singlePaymentFee &&
          this.renderSinglePaymentFee({ utilities: ['smHidden'] })
        }
        <div className={suitcss({ descendantName: 'buttons' }, this)} >
          <div className={suitcss({ descendantName: 'button' }, this)} >
            <Link
              asButton
              buttonFilled
              to={linkPrimary.to}
              utilities={['sHidden']}
            >
              {linkPrimary.label}
            </Link>
            <Link
              asButton
              buttonFilled
              buttonWithoutLabel
              to={linkPrimary.to}
              utilities={['mlHidden']}
            >
              {linkPrimary.label}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { inverted, tariff } = this.props;
    return (
      <section
        className={suitcss({
          modifiers: [inverted && 'inverted'],
        }, this)}
        data-tracking="tariff"
        data-tariff-id={tariff.iid}
      >
        {this.renderHeader()}
        {this.renderContent()}
        {this.renderSubContent()}
        <Callout
          theme="badge"
          targets={[tariff.eid]}
          inverted={inverted}
        />
      </section>
    );
  }
}

TariffItemPanel.propTypes = {
  tariff: tariffShape.isRequired,
  moduleId: PropTypes.string.isRequired,
  inverted: PropTypes.bool,
  linkPrimary: PropTypes.shape({
    label: PropTypes.string,
    to: PropTypes.string,
  }).isRequired,
  ui: PropTypes.shape({
    guiFeeSingular: PropTypes.string,
  }),
  productInformationSheetLink: PropTypes.object,
};

export default connectUI()(TariffItemPanel);
